/**
 * The facade entry points: {@link configureLogging} (app-init), {@link createLogger} (per-module),
 * and {@link setLevel} (runtime). A single root engine is created lazily; `createLogger` returns
 * namespaced sub-loggers, and `child()` binds additional context without a new namespace.
 */
import { createEngine } from './engine/tslog-engine.js';
import { resolveInitialLevel } from './level.js';
import { buildMaskSettings } from './redaction.js';
import { createSentryTransport } from './transports/sentry-transport.js';
const DEFAULT_ROOT_NAMESPACE = 'app';
let engine;
let rootLevel = resolveInitialLevel();
let configured = false;
function isDev() {
    const env = typeof process !== 'undefined' && process.env ? process.env['NODE_ENV'] : undefined;
    return env !== 'production';
}
function ensureEngine() {
    if (!engine) {
        engine = createEngine({
            namespace: DEFAULT_ROOT_NAMESPACE,
            level: rootLevel,
            prettyConsole: isDev(),
            mask: buildMaskSettings(),
        });
    }
    return engine;
}
/**
 * Configure the root logger once, at app init. Registers the Sentry breadcrumb transport and/or the
 * OTLP→SigNoz transport, sets the central redaction policy, and resolves the starting level
 * (a `localStorage` override wins over `config.level`, which wins over the env default).
 */
export function configureLogging(config = {}) {
    // Idempotency guard, mirroring `initializeSentry`'s `if (Sentry.getClient()) return`. The v8 app
    // calls this at module scope in `app.tsx`, which re-runs on Fast Refresh; without this, each HMR
    // re-registers the Sentry/OTLP transports and every log line double-reports. `resetLogging()`
    // clears it for tests that need a fresh root.
    if (configured)
        return;
    rootLevel = resolveInitialLevel(config.level);
    const created = createEngine({
        namespace: DEFAULT_ROOT_NAMESPACE,
        level: rootLevel,
        prettyConsole: config.prettyConsole ?? isDev(),
        mask: buildMaskSettings(config.redaction),
    });
    if (config.sentry)
        created.addFacadeTransport(createSentryTransport(config.sentry));
    for (const transport of config.transports ?? [])
        created.addFacadeTransport(transport);
    if (config.otel) {
        // Load the OTLP→SigNoz transport lazily: it pulls in `tslog/otel` + the HTTP transport, which
        // are dead weight in the v8 initial bundle whenever OTel is unconfigured (the common case, gated
        // on OTEL_EXPORTER_OTLP_ENDPOINT). A static import cannot be tree-shaken because it is referenced
        // in this runtime conditional. Attaching a microtask later is fine for a background log exporter.
        const otel = config.otel;
        void import('./transports/otel-transport.js')
            .then(({ createOtelTransport, createOtelTraceMiddleware }) => {
            created.attachNative(createOtelTransport(otel));
            if (otel.getSpanContext)
                created.use(createOtelTraceMiddleware(otel.getSpanContext));
        })
            .catch((err) => {
            // Never fail silently: a failed dynamic import (network hiccup, bad chunk) would otherwise
            // leave OTel permanently unattached with zero signal.
            console.error('Failed to load/attach the OTLP→SigNoz transport:', err);
        });
    }
    engine = created;
    configured = true;
}
/** Reset root configuration. Test-only: lets a suite reconfigure the root logger from scratch. */
export function resetLogging() {
    engine = undefined;
    configured = false;
    rootLevel = resolveInitialLevel();
}
/** Raise/lower the global level at runtime (support/QA). Persist across reloads via localStorage. */
export function setLevel(level) {
    rootLevel = level;
    ensureEngine().setMinLevel(level);
}
function mergeContext(bound, extra) {
    const merged = { ...bound, ...(extra ?? {}) };
    return Object.keys(merged).length > 0 ? merged : undefined;
}
function makeFacade(tsLogger, boundContext) {
    const emit = (method, message, context) => {
        const ctx = mergeContext(boundContext, context);
        if (ctx)
            tsLogger[method](message, ctx);
        else
            tsLogger[method](message);
    };
    const emitError = (method, message, error, context) => {
        const ctx = mergeContext(boundContext, context);
        if (error instanceof Error) {
            if (ctx)
                tsLogger[method](message, error, ctx);
            else
                tsLogger[method](message, error);
        }
        else if (error !== undefined) {
            tsLogger[method](message, { ...(ctx ?? {}), error });
        }
        else if (ctx) {
            tsLogger[method](message, ctx);
        }
        else {
            tsLogger[method](message);
        }
    };
    return {
        silly: (message, context) => emit('silly', message, context),
        trace: (message, context) => emit('trace', message, context),
        debug: (message, context) => emit('debug', message, context),
        info: (message, context) => emit('info', message, context),
        warn: (message, context) => emit('warn', message, context),
        error: (message, error, context) => emitError('error', message, error, context),
        fatal: (message, error, context) => emitError('fatal', message, error, context),
        child: (context) => makeFacade(tsLogger, { ...boundContext, ...context }),
        flush: () => ensureEngine().flush(),
    };
}
/**
 * Create a namespaced logger, e.g. `createLogger('auth:apollo')`. Namespaces are colon-delimited and
 * chain through `child()`-derived sub-loggers in the underlying engine.
 */
export function createLogger(namespace, options = {}) {
    const tsLogger = ensureEngine().getSubLogger(namespace, options.minLevel);
    return makeFacade(tsLogger, options.context ?? {});
}
